/**
 * What the viewer may do: the school permissions their active-school
 * membership grants (from `/v1/users/me`) and the platform permissions their
 * Clerk session carries. The server guard (`require-surface.ts`), the client
 * `SchoolProvider` and the nav resolver all build and read it here.
 *
 * Client-safe: no server-only imports.
 */

import { CapabilityKeySchema, type CapabilityKey, type PlatformPermission } from '@school-journey/shared-types'

export interface ViewerAccess {
  school: ReadonlySet<CapabilityKey>
  platform: ReadonlySet<PlatformPermission>
}

/** One permission an area or nav entry requires. */
export type AccessRequirement = { school: CapabilityKey } | { platform: PlatformPermission }

const ALL_SCHOOL_KEYS: readonly CapabilityKey[] = CapabilityKeySchema.options

/** The slice of a `/v1/users/me` membership this module reads. */
interface MembershipLike {
  status?: string | null
  roles?: { isAdmin?: boolean } | null
  permissions?: readonly string[]
}

/**
 * The school permissions one `/v1/users/me` membership grants. Strings that are
 * not known school keys are dropped, so a key added on the backend first is
 * ignored rather than fatal.
 *
 * Skew fallback (AE4): a backend that predates `permissions`, or failed to
 * resolve them, omits the field. Then School Admin is read the old way, ACTIVE
 * and `roles.isAdmin`, and holds every key. Remove once no deploy or rollback
 * can pair this web with such a backend.
 */
export function membershipPermissions(membership: MembershipLike): CapabilityKey[] {
  if (membership.permissions) {
    return membership.permissions.filter((key): key is CapabilityKey => CapabilityKeySchema.safeParse(key).success)
  }
  const isSchoolAdmin = membership.roles?.isAdmin === true && membership.status === 'ACTIVE'
  return isSchoolAdmin ? [...ALL_SCHOOL_KEYS] : []
}

/**
 * Composes the viewer. Super Admin (the `isSuperAdmin` claim) is today's only
 * platform role and holds `operate_platform`.
 */
export function buildViewerAccess(input: {
  isSuperAdmin: boolean
  schoolPermissions: readonly CapabilityKey[]
}): ViewerAccess {
  return {
    school: new Set(input.schoolPermissions),
    platform: new Set<PlatformPermission>(input.isSuperAdmin ? ['operate_platform'] : [])
  }
}

/**
 * Whether the viewer meets `requirement`. A school requirement also passes
 * for `operate_platform`: a super-admin holds every school permission at every
 * school, membership or not (KTD10).
 */
export function holds(viewer: ViewerAccess, requirement: AccessRequirement): boolean {
  if ('platform' in requirement) return viewer.platform.has(requirement.platform)
  return viewer.school.has(requirement.school) || viewer.platform.has('operate_platform')
}
